import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Calendar, Users, Edit, Trash2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Calendars({ token, user, onLogout }) {
  usePageTitle('Calendários');
  const navigate = useNavigate();
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, calendarId: null });
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [shareEmail, setShareEmail] = useState('');
  const [shareReadOnly, setShareReadOnly] = useState(false);
  const [shares, setShares] = useState([]);

  useEffect(() => {
    fetchCalendars();
  }, []);

  const fetchCalendars = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/calendars`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCalendars(data);
      }
    } catch (error) {
      toast.error('Erro ao carregar calendários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingCalendar 
        ? `${BACKEND_URL}/api/calendars/${editingCalendar.id}`
        : `${BACKEND_URL}/api/calendars`;
      
      const response = await fetch(url, {
        method: editingCalendar ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingCalendar ? 'Calendário atualizado!' : 'Calendário criado!');
        setDialogOpen(false);
        setFormData({ name: '', description: '' });
        setEditingCalendar(null);
        fetchCalendars();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao salvar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/calendars/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Calendário excluído!');
        fetchCalendars();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
    setDeleteDialog({ open: false, calendarId: null });
  };

  const openShareDialog = async (calendar) => {
    setSelectedCalendar(calendar);
    setShareDialogOpen(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/calendars/${calendar.id}/shares`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setShares(data);
      }
    } catch (error) {
      toast.error('Erro ao carregar compartilhamentos');
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/calendars/${selectedCalendar.id}/shares`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_email: shareEmail, read_only: shareReadOnly })
      });

      if (response.ok) {
        toast.success('Calendário compartilhado!');
        setShareEmail('');
        setShareReadOnly(false);
        openShareDialog(selectedCalendar);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao compartilhar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const handleRemoveShare = async (shareId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/calendars/${selectedCalendar.id}/shares/${shareId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Compartilhamento removido!');
        openShareDialog(selectedCalendar);
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>Calendários</h1>
            <p>Gerencie seus calendários e compartilhe com sua equipe</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingCalendar(null); setFormData({ name: '', description: '' }); }} data-testid="create-calendar-button">
                <Plus size={18} style={{ marginRight: '0.5rem' }} />
                Novo Calendário
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingCalendar ? 'Editar Calendário' : 'Novo Calendário'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="calendar-name-input"
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="calendar-description-input"
                  />
                </div>
                <Button type="submit" data-testid="save-calendar-button">Salvar</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div>Carregando...</div>
      ) : calendars.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>
          <Calendar size={64} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
          <p>Nenhum calendário encontrado. Crie seu primeiro calendário!</p>
        </div>
      ) : (
        <div className="calendar-grid">
          {calendars.map((calendar) => (
            <Card key={calendar.id} data-testid={`calendar-card-${calendar.id}`}>
              <CardHeader>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <CardTitle>{calendar.name}</CardTitle>
                    <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
                      {calendar.description || 'Sem descrição'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {calendar.is_owner ? (
                      <Badge variant="default">Proprietário</Badge>
                    ) : calendar.read_only ? (
                      <Badge variant="secondary">Somente Leitura</Badge>
                    ) : (
                      <Badge variant="outline">Colaborador</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem' }}>
                  Proprietário: {calendar.owner_name}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/calendar/${calendar.id}`)}
                    data-testid={`view-calendar-${calendar.id}`}
                  >
                    Visualizar
                  </Button>
                  {calendar.is_owner && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setEditingCalendar(calendar); setFormData({ name: calendar.name, description: calendar.description }); setDialogOpen(true); }}
                        data-testid={`edit-calendar-${calendar.id}`}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openShareDialog(calendar)}
                        data-testid={`share-calendar-${calendar.id}`}
                      >
                        <Share2 size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteDialog({ open: true, calendarId: calendar.id })}
                        data-testid={`delete-calendar-${calendar.id}`}
                        style={{ color: '#ef4444' }}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Compartilhar Calendário</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleShare} style={{ marginBottom: '1.5rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <Label htmlFor="shareEmail">Email do usuário</Label>
              <Input
                id="shareEmail"
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                required
                data-testid="share-email-input"
              />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="shareReadOnly"
                checked={shareReadOnly}
                onChange={(e) => setShareReadOnly(e.target.checked)}
                data-testid="share-readonly-checkbox"
              />
              <Label htmlFor="shareReadOnly">Somente leitura</Label>
            </div>
            <Button type="submit" data-testid="share-submit-button">Compartilhar</Button>
          </form>

          <div>
            <h4 style={{ marginBottom: '1rem', fontWeight: 600 }}>Compartilhado com:</h4>
            {shares.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Ainda não compartilhado</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {shares.map((share) => (
                  <div key={share.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f8fafc', borderRadius: '8px' }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{share.user_name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{share.user_email}</div>
                      {share.read_only && <Badge variant="secondary" style={{ marginTop: '0.25rem' }}>Somente Leitura</Badge>}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveShare(share.id)}
                      style={{ color: '#ef4444' }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, calendarId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este calendário? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteDialog.calendarId)} style={{ background: '#ef4444' }}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
